import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { useBrokers } from '@/providers/brokerProvider'

type UseSessionTokenVerificationOptions = {
	mode: 'require' | 'redirect-if-present' | 'optional'
	redirectTo?: string
}

type UseSessionTokenVerificationResult = {
	token: string
	isVerifying: boolean
}

function useSessionTokenVerification({
	mode,
	redirectTo,
}: UseSessionTokenVerificationOptions): UseSessionTokenVerificationResult {
	const [cookies, , removeCookie] = useCookies(['session_token'])
	const brokers = useBrokers()
	const [token, setToken] = useState<string>('')
	const [isVerifying, setIsVerifying] = useState(true)

	useEffect(() => {
		let cancelled = false

		const validateToken = async () => {
			const sessionToken = cookies['session_token'] || ''

			if (!sessionToken) {
				if (mode === 'require') {
					removeCookie('session_token', { path: '/' })
					window.location.href = redirectTo ?? ''
				}
				if (!cancelled) setIsVerifying(false)
				return
			}

			try {
				const response = await brokers.client.verifyToken({ token: sessionToken })
				if (response.valid) {
					if (!cancelled) setToken(sessionToken)
					if (mode === 'redirect-if-present') {
						window.location.href = redirectTo ?? ''
					}
				} else if (mode === 'require') {
					removeCookie('session_token', { path: '/' })
					window.location.href = redirectTo ?? ''
				}
			} catch (error) {
				console.error('Error verifying token: ', error)
				if (mode === 'require') {
					removeCookie('session_token', { path: '/' })
					window.location.href = redirectTo ?? ''
				}
			} finally {
				if (!cancelled) setIsVerifying(false)
			}
		}

		validateToken()

		return () => {
			cancelled = true
		}
	}, [brokers.client, cookies, mode, redirectTo, removeCookie])

	return { token, isVerifying }
}

type UseSessionTokenCookieResult = {
	setSessionToken: (token: string) => void
	clearSessionToken: () => void
}

function useSessionTokenCookie(): UseSessionTokenCookieResult {
	const [, setCookie, removeCookie] = useCookies(['session_token'])

	const setSessionToken = (value: string) => {
		setCookie('session_token', value, { path: '/' })
	}

	const clearSessionToken = () => {
		removeCookie('session_token', { path: '/' })
	}

	return { setSessionToken, clearSessionToken }
}

export { useSessionTokenVerification, useSessionTokenCookie }
